import { motion } from 'framer-motion'
import { ArrowLeft } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { EntryCard } from '../components/Entry/EntryCard'
import { getNostrPool } from '../lib/nostrPool'
import { RELAYS } from '../config/constants'
import { useReplies } from '../hooks/useReplies'

import { CompactReply } from '../components/Entry/CompactReply'
import { generateTopicUrl } from '../lib/urlUtils'
import { useReactions } from '../hooks/useReactions'

export function EntryPage() {
    const { entryId } = useParams<{ entryId: string }>()
    const pool = getNostrPool()
    const { replies, isLoading: isRepliesLoading } = useReplies(entryId || null)

    const { data: entry, isLoading, error } = useQuery({
        queryKey: ['entry', entryId],
        queryFn: async () => {
            if (!entryId) throw new Error('Entry ID is required')

            const event = await pool.get(RELAYS, {
                ids: [entryId]
            })

            if (!event) throw new Error('Entry bulunamadı')

            // Map Nostr event to Entry interface
            const subjectTag = event.tags.find(t => t[0] === 'subject')?.[1]
            const topicTag = event.tags.find(t => t[0] === 't' && t[1] !== 'mavililer' && t[1] !== 'mavililersözlük')?.[1]

            // Clean title: prefer subject, fallback to t tag, always remove dashes
            const rawTitle = subjectTag || topicTag || 'Başlıksız'
            const cleanTitle = rawTitle.replace(/-/g, ' ')

            return {
                id: event.id,
                content: event.content,
                created_at: new Date(event.created_at * 1000).toISOString(),
                author_username: event.pubkey.slice(0, 8),
                author_pubkey: event.pubkey,
                author_avatar_url: null,
                author_reputation: 0,
                upvotes: 0,
                downvotes: 0,
                topicTitle: cleanTitle,
                rawEvent: event
            }
        },
        enabled: !!entryId,
        retry: false,
    })


    // Fetch live reactions - using entryId from params allows earlier fetching and better stability
    const { upvotes, downvotes, userVotes } = useReactions(entryId || null)

    if (isLoading) {
        return (
            <div className="animate-pulse space-y-6">
                <div className="h-8 bg-surface-200 dark:bg-surface-700 rounded w-1/2"></div>
                <div className="h-48 bg-surface-200 dark:bg-surface-700 rounded"></div>
            </div>
        )
    }

    if (error || !entry) {
        return (
            <div className="text-center py-12">
                <h2 className="font-poppins font-semibold text-2xl mb-4">Entry Bulunamadı</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Aradığınız entry mevcut değil veya silinmiş olabilir. (Nostr)
                </p>
                <Link to="/" className="btn-primary">
                    Ana Sayfaya Dön
                </Link>
            </div>
        )
    }


    // Merge live stats with entry data
    const entryWithStats = entry ? {
        ...entry,
        upvotes: upvotes,
        downvotes: downvotes,
        userVotes: userVotes
    } : null

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
        >
            {/* Header */}
            <div className="flex items-center gap-4">
                <Link
                    to="/"
                    className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                </Link>
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                        <h1 className="font-poppins font-bold text-2xl lg:text-3xl text-gray-900 dark:text-white">
                            {entry.topicTitle}
                        </h1>
                    </div>
                </div>
            </div>

            {/* Main Entry */}
            {entryWithStats && (
                <EntryCard entry={entryWithStats} showPermalink={true} hideTopicHeader={true} />
            )}

            {/* Replies Section */}
            <div className="space-y-4 pt-6">
                {/* Section Header with Visual Connector */}
                <div className="flex items-center gap-3 px-1">
                    <div className="flex items-center gap-2">
                        <div className="w-1 h-8 bg-gradient-to-b from-mavi-500/50 to-transparent rounded-full" />
                        <h2 className="font-poppins font-bold text-lg text-gray-900 dark:text-white">
                            Yanıtlar
                        </h2>
                    </div>
                    {replies.length > 0 && (
                        <span className="text-xs font-medium text-gray-400 bg-surface-100 dark:bg-surface-800 px-2 py-0.5 rounded-full">
                            {replies.length}
                        </span>
                    )}
                </div>

                {isRepliesLoading ? (
                    <div className="space-y-3">
                        {[1, 2].map(i => (
                            <div key={i} className="animate-pulse flex gap-3 p-3">
                                <div className="w-10 h-10 bg-surface-200 dark:bg-surface-700 rounded-full" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 bg-surface-200 dark:bg-surface-700 rounded w-1/4" />
                                    <div className="h-3 bg-surface-200 dark:bg-surface-700 rounded w-3/4" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : replies.length === 0 ? (
                    <div className="text-center py-8 px-4 bg-surface-50/50 dark:bg-surface-900/30 rounded-xl border border-dashed border-surface-200 dark:border-surface-700">
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                            Henüz bu entry'ye yanıt verilmemiş.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-1">
                        {replies.map((reply, index) => (
                            <CompactReply
                                key={reply.id}
                                reply={reply}
                                isLast={index === replies.length - 1}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Minimal Entry Info Bar + CTA */}
            <div className="mt-8 space-y-4 flex flex-col items-center">


                {/* CTA Button */}
                <div className="flex flex-col items-center gap-2">
                    <Link
                        to={generateTopicUrl(entry.topicTitle, entry.topicTitle)}
                        className="px-8 py-3 bg-mavi-500 hover:bg-mavi-600 text-white rounded-xl font-poppins font-bold transition-all shadow-lg shadow-mavi-500/20 active:scale-95 text-sm"
                    >
                        Başlığın Tamamını Gör
                    </Link>

                </div>
            </div>
        </motion.div>
    )
}

